import { SearchOutlined } from '@ant-design/icons'
import {
    Button,
    ConfigProvider,
    Form,
    Input,
    Modal,
    Popover,
    RadioChangeEvent,
    Space,
    Spin,
    Table,
    theme,
} from 'antd'
import { useForm } from 'antd/es/form/Form'
import {
    createContext,
    FC,
    FocusEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import Stamp from '../../../types/api/stamp'
import { Filters } from '../../../types/strapi/strapi'
import { useGetStampByDocIdQuery, useLazyGetStampsQuery } from '../../../store/api/stamps-api'
import _ from 'lodash'
import { columns } from './columns'
import { useGetRollersQuery } from '../../../store/api/rollers-api'
import Roller from '../../../types/api/roller'
import FormValues from './types/form-values'
import Shape from './components/form-items/shape'
import Rollers from './components/form-items/rollers'
import Width from './components/form-items/width'
import Length from './components/form-items/length'
import Floods from './components/form-items/floods'
import Cost from './components/form-items/cost'
import SameStamps from './components/same-stamps'
import UseNewButton from './components/use-new-button'
import HalfRotation from './components/form-items/half-rotation'
import { HALF_ROTATION_ROLLER } from '../../../helpers/format-roller-display'

type ContextValue = { onSelectStamp: (stamp: Stamp) => void }
export const StampSearchTableContext = createContext<ContextValue>({ onSelectStamp: () => {} })

export type NewStamp = {
    width: number
    length: number
    roller: string
    floods: number
    shape: string
}

interface StampSearchControlProps {
    value?: string
    onChange?: (stampDocId?: string) => void
    autoFocus?: boolean
    onBlurOfFullField?: () => void
}

const StampSearchControl: FC<StampSearchControlProps> = ({
    value,
    onChange = () => {},
    onBlurOfFullField = () => {},
    autoFocus,
}) => {
    const [open, setOpen] = useState<boolean>(false)
    const [inputValue, setInputValue] = useState('')
    const [idSearchResults, setIdSearchResults] = useState<Stamp[] | null>(null)
    const [idSearchLoading, setIdSearchLoading] = useState(false)
    const [form] = useForm<FormValues>()
    const [searchStampsById] = useLazyGetStampsQuery()
    const [searchStampsBySize] = useLazyGetStampsQuery()
    const { data: stamp } = useGetStampByDocIdQuery([value || ''], { skip: !value })
    const { data: rollersData } = useGetRollersQuery({
        filters: { archived: { $ne: true } },
        populate: { machines: { populate: { machine_brand: true } } },
        pagination: { pageSize: 100, page: 1 },
    })
    const rollers = useMemo(() => rollersData?.data || [], [rollersData?.data])
    const [sizeSearchResult, setSizeSearchResult] = useState<{
        halfRotation: boolean
        stamps: Stamp[]
    } | null>(null)
    const [sizeSearchPending, setSizeSearchPending] = useState(false)
    const sizeSearchSeqRef = useRef(0)
    const sizeSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const inputValueRef = useRef(inputValue)
    inputValueRef.current = inputValue

    const stampData = stamp?.data
    const lengthWatch = Form.useWatch('length', form)
    const halfRotation = Boolean(Form.useWatch('halfRotation', form))

    const halfRotationRollerDocIds = useMemo(
        () =>
            rollers
                .filter(r => Number(r.roller) === HALF_ROTATION_ROLLER)
                .map(r => r.documentId),
        [rollers]
    )

    useEffect(() => {
        if (stampData) {
            setInputValue(stampData.number || '')
        } else if (!value) {
            setInputValue('')
        }
        // Завязано на номер штампа, а не на объект: перезапрос штампа не должен сбрасывать ввод
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, stampData?.number])

    const searchByIdOrNumber = useCallback(
        async (searchValue: string) => {
            const trimmed = searchValue.trim()
            if (!trimmed) {
                setIdSearchResults(null)
                setIdSearchLoading(false)
                return
            }

            if (
                stampData &&
                (trimmed === String(stampData.id) || trimmed === stampData.number)
            ) {
                setIdSearchResults(null)
                setIdSearchLoading(false)
                return
            }

            const orFilters: Record<string, { $eq: string | number }>[] = [
                { number: { $eq: trimmed } },
            ]
            const numericId = Number(trimmed)
            if (!Number.isNaN(numericId) && String(numericId) === trimmed) {
                orFilters.unshift({ id: { $eq: numericId } })
            }

            setIdSearchLoading(true)

            try {
                const result = await searchStampsById({
                    filters: { $or: orFilters } as Filters,
                    populate: '*',
                }).unwrap()

                if (inputValueRef.current !== searchValue) return

                setIdSearchResults(result.data || [])
            } catch (e) {
                console.error(e)
                if (inputValueRef.current === searchValue) {
                    setIdSearchResults([])
                }
            } finally {
                if (inputValueRef.current === searchValue) {
                    setIdSearchLoading(false)
                }
            }
        },
        [searchStampsById, stampData]
    )

    const debouncedSearchByIdOrNumber = useMemo(
        () => _.debounce((searchValue: string) => searchByIdOrNumber(searchValue), 600),
        [searchByIdOrNumber]
    )

    useEffect(() => {
        debouncedSearchByIdOrNumber(inputValue)
        return () => debouncedSearchByIdOrNumber.cancel()
    }, [inputValue, debouncedSearchByIdOrNumber])

    const onInputChange = (nextValue: string) => {
        setInputValue(nextValue)
        setIdSearchResults(null)
        setIdSearchLoading(false)
    }

    const onBlurControl: FocusEventHandler<HTMLElement> = e => {
        const relatedTarget = e.relatedTarget as HTMLElement | null
        const sscId = relatedTarget?.getAttribute('data-ssc-id')
        const relatedInPopover = relatedTarget?.closest?.('[data-ssc-id="id-search-popover"]')

        if (
            sscId === 'output-input' ||
            sscId === 'search-button' ||
            sscId === 'id-search-popover' ||
            relatedInPopover ||
            open
        ) {
            // если перефокусируется на контрол в этом же елементе, то все норм, не считаем за onBlur
            return
        }

        const matchesCurrent =
            stampData &&
            (inputValue === String(stampData.id) || inputValue === stampData.number)
        if (!matchesCurrent) {
            if (stampData) {
                setInputValue(stampData.number || '')
            } else {
                setInputValue('')
            }
        }

        setIdSearchResults(null)
        onBlurOfFullField()
    }

    const closeModal = () => {
        setOpen(false)
        onBlurOfFullField()
    }

    const scheduleSizeSearch = useCallback(
        (
            width: number | undefined | null,
            length: number | undefined | null,
            shape: string | undefined,
            onlyHalfRotation?: boolean
        ) => {
            if (sizeSearchTimerRef.current) clearTimeout(sizeSearchTimerRef.current)

            const seq = ++sizeSearchSeqRef.current
            const halfRotationSearch = Boolean(onlyHalfRotation)

            if (!width || !length) {
                setSizeSearchPending(false)
                return
            }

            setSizeSearchPending(true)

            sizeSearchTimerRef.current = setTimeout(() => {
                if (sizeSearchSeqRef.current !== seq) return

                if (halfRotationSearch && rollersData === undefined) return

                if (halfRotationSearch && halfRotationRollerDocIds.length === 0) {
                    setSizeSearchResult({ halfRotation: true, stamps: [] })
                    setSizeSearchPending(false)
                    return
                }

                searchStampsBySize({
                    filters: {
                        width: { $eq: width },
                        length: { $eq: length },
                        shape: { $contains: shape },
                        ...(halfRotationSearch
                            ? { roller: { documentId: { $in: halfRotationRollerDocIds } } }
                            : {}),
                    },
                    populate: '*',
                })
                    .unwrap()
                    .then(result => {
                        if (sizeSearchSeqRef.current !== seq) return

                        const list = result.data
                        setSizeSearchResult({
                            halfRotation: halfRotationSearch,
                            stamps: halfRotationSearch
                                ? list.filter(
                                      s => Number(s.roller?.roller) === HALF_ROTATION_ROLLER
                                  )
                                : list,
                        })
                        setSizeSearchPending(false)
                    })
                    .catch(e => {
                        console.error(e)
                        if (sizeSearchSeqRef.current !== seq) return
                        setSizeSearchResult({ halfRotation: halfRotationSearch, stamps: [] })
                        setSizeSearchPending(false)
                    })
                // TODO: Брать только не заблокированные
            }, 300)
        },
        [searchStampsBySize, halfRotationRollerDocIds, rollersData]
    )

    useEffect(
        () => () => {
            if (sizeSearchTimerRef.current) clearTimeout(sizeSearchTimerRef.current)
        },
        []
    )

    const onChangeWidth = (width: number | null) => {
        const values = form.getFieldsValue()
        scheduleSizeSearch(width, values.length, values.shape, Boolean(values.halfRotation))
    }

    const onChangeLength = (length: number | null) => {
        const values = form.getFieldsValue()
        scheduleSizeSearch(values.width, length, values.shape, Boolean(values.halfRotation))
    }

    const onChangeShape = (e: RadioChangeEvent) => {
        console.log(e.target)
        const values = form.getFieldsValue()
        scheduleSizeSearch(values.width, values.length, e.target.value, Boolean(values.halfRotation))
    }

    const onSelectStamp = (selectedStamp: Stamp) => {
        onChange(selectedStamp.documentId)
        form.resetFields()
        closeModal()
    }

    const onSelectStampFromIdSearch = (selectedStamp: Stamp) => {
        onChange(selectedStamp.documentId)
        setIdSearchResults(null)
        onBlurOfFullField()
    }

    const trimmedInput = inputValue.trim()
    const matchesCurrentStamp =
        stampData &&
        (trimmedInput === String(stampData.id) || trimmedInput === stampData.number)

    const idSearchPopoverOpen =
        trimmedInput !== '' &&
        !matchesCurrentStamp &&
        (idSearchLoading || idSearchResults !== null)

    const idSearchPopoverContent = (
        <div data-ssc-id={'id-search-popover'} onClick={e => e.stopPropagation()}>
            {idSearchLoading ? (
                <Spin size={'small'} />
            ) : idSearchResults?.length === 0 ? (
                'Штамп не найден'
            ) : idSearchResults && idSearchResults.length > 0 ? (
                <>
                    {idSearchResults.length > 1 ? (
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Найдено несколько штампов
                        </div>
                    ) : null}
                    <ConfigProvider theme={{ algorithm: theme.compactAlgorithm }}>
                        <StampSearchTableContext.Provider
                            value={{ onSelectStamp: onSelectStampFromIdSearch }}
                        >
                            <Table
                                dataSource={idSearchResults}
                                columns={columns}
                                pagination={false}
                                rowKey={x => x.id}
                                size={'small'}
                            />
                        </StampSearchTableContext.Provider>
                    </ConfigProvider>
                </>
            ) : null}
        </div>
    )

    const stamps =
        sizeSearchResult?.halfRotation === halfRotation ? sizeSearchResult.stamps : []

    const displayedVariants = useMemo(() => {
        const length = lengthWatch || 1
        const list: {
            distance: number
            numberOfLabels: number
            roller: Roller
        }[] = []

        const sourceRollers = halfRotation
            ? rollers.filter(r => Number(r.roller) === HALF_ROTATION_ROLLER)
            : rollers

        sourceRollers.forEach(r => {
            let maxOfLabels = Math.floor(Number(r.roller) / length)
            let numberOfLabels = maxOfLabels
            for (let j = 1; j <= 4 && numberOfLabels !== 1; j++) {
                const distance = (Number(r.roller) - numberOfLabels * length) / numberOfLabels
                list.push({
                    distance,
                    numberOfLabels,
                    roller: r,
                })
                numberOfLabels -= 1
            }
        })

        return list.sort((a, b) => a.distance - b.distance)
    }, [rollers, lengthWatch, halfRotation])

    useEffect(() => {
        if (halfRotation) {
            const rollerValue = form.getFieldValue('roller')
            if (rollerValue) {
                try {
                    const parsed = JSON.parse(rollerValue) as { roller?: string }
                    const selectedRoller = rollers.find(r => r.documentId === parsed.roller)
                    if (Number(selectedRoller?.roller) !== HALF_ROTATION_ROLLER) {
                        form.setFieldValue('roller', undefined)
                    }
                } catch {
                    form.setFieldValue('roller', undefined)
                }
            }
        }

        const values = form.getFieldsValue()
        scheduleSizeSearch(values.width, values.length, values.shape, halfRotation)
    }, [halfRotation, halfRotationRollerDocIds, form, rollers, scheduleSizeSearch])

    return (
        <div onClick={e => e.stopPropagation()}>
            <Popover
                open={idSearchPopoverOpen}
                placement={'bottomLeft'}
                arrow={false}
                trigger={[]}
                content={idSearchPopoverContent}
                styles={{ body: { width: 900, maxHeight: 400, overflow: 'auto' } }}
            >
                <span>
                    <Space.Compact>
                        <Input
                            data-ssc-id={'output-input'}
                            value={inputValue}
                            autoFocus={autoFocus}
                            onChange={e => onInputChange(e.target.value)}
                            onBlur={onBlurControl}
                        />
                        <Button
                            data-ssc-id={'search-button'}
                            icon={<SearchOutlined />}
                            onClick={() => setOpen(true)}
                            onBlur={onBlurControl}
                        />
                    </Space.Compact>
                </span>
            </Popover>

            <Modal
                style={{ top: 24 }}
                title={'Поиск штампа'}
                open={open}
                width={1300}
                footer={null}
                onCancel={() => {
                    closeModal()
                }}
            >
                {/* 
                Выводим модалку с поиском, если список пуст, 
                то выводится выбор ручьев и межэтикетки и стоимость нового с кнопкой "Использовать новый" 
                */}
                <Form form={form} layout={'vertical'}>
                    <Space size={24} align={'start'}>
                        <Width onChange={onChangeWidth} />
                        <Length onChange={onChangeLength} />
                        <Shape onChange={onChangeShape} />
                        <HalfRotation />
                    </Space>
                    <Form.Item
                        shouldUpdate={(prev, next) =>
                            prev.shape !== next.shape || prev.halfRotation !== next.halfRotation
                        }
                        noStyle
                    >
                        {form => {
                            const show =
                                form.getFieldValue('shape') === 'Фигурный' ||
                                (!sizeSearchPending && stamps.length === 0)

                            if (!show) return null

                            return (
                                <Space size={24} align={'start'}>
                                    <Floods />
                                    <Rollers variants={displayedVariants} />
                                    {/* <RollWidth /> */}
                                    <Cost />
                                    <UseNewButton onSelect={onSelectStamp} />
                                </Space>
                            )
                        }}
                    </Form.Item>
                    {stamps.length > 0 ? (
                        <ConfigProvider theme={{ algorithm: theme.compactAlgorithm }}>
                            <StampSearchTableContext.Provider value={{ onSelectStamp }}>
                                <Table
                                    dataSource={stamps}
                                    columns={columns}
                                    pagination={false}
                                    rowKey={x => x.id}
                                />
                            </StampSearchTableContext.Provider>
                        </ConfigProvider>
                    ) : sizeSearchPending ? null : (
                        <SameStamps
                            onSelectStamp={onSelectStamp}
                            halfRotation={halfRotation}
                            halfRotationRollerDocIds={
                                rollersData ? halfRotationRollerDocIds : undefined
                            }
                        />
                    )}
                    <br />
                </Form>
            </Modal>
        </div>
    )
}

export default StampSearchControl
